import { MetadataType } from '@aibees/elephant/hooks';
import { SearchType } from '@aibees/elephant/types';
import {
  FilterBadgeType,
  FilterCategory,
  SearchFilter,
  SearchFilterBase,
  SearchFilterType,
} from '../types';

const SOCIAL_PLATFORMS = [
  {
    label: 'LinkedIn',
    value: 'linkedin',
    enabled: true,
    open: true,
  },
  {
    label: 'Facebook',
    value: 'facebook',
    enabled: false,
    open: false,
  },
  {
    label: 'X (Twitter)',
    value: 'x',
    enabled: false,
    open: false,
  },
  {
    label: 'Instagram',
    value: 'instagram',
    enabled: false,
    open: false,
  },
];

type Platform = (typeof SOCIAL_PLATFORMS)[number];
type Entity = 'companies' | 'people';

const entityLabelOf = (entity: Entity) =>
  entity === 'companies' ? 'Companies' : 'People';

export default function socialMediaFollowersFilterConfig(
  type: SearchType
): SearchFilterBase {
  const isCompany = type === SearchType.Company;

  const scopePrefixFor = (entity: Entity): 'company' | 'person' =>
    entity === 'companies' ? 'company' : 'person';

  const metadataKeyFor = (
    platform: Platform,
    entity: Entity
  ): MetadataType | undefined => {
    if (platform.value !== 'linkedin') return undefined;
    return entity === 'companies'
      ? MetadataType.COMPANY_LINKEDIN_FOLLOWERS
      : MetadataType.PERSON_LINKEDIN_FOLLOWERS;
  };

  const buildFollowersRadioGroup = (
    platform: Platform,
    entity: Entity
  ): SearchFilter => {
    const prefix = scopePrefixFor(entity);
    const metaDataApiKey = metadataKeyFor(platform, entity);
    return {
      name: '',
      type: SearchFilterType.RADIO_GROUP,
      index: 0,
      key: `${prefix}_${platform.value}_followers_range_type`,
      field: `${prefix}_${platform.value}_followers_range_type`,
      sections: { main: 2, more: 1 },
      items: [
        {
          name: 'Predefined Range',
          type: SearchFilterType.CHECK_BOX,
          index: 0,
          layer: 4,
          key: `${prefix}_${platform.value}_followers_predefined`,
          field: `${prefix}_${platform.value}_followers_predefined`,
          metadata: {
            color: 'primary',
            ...(metaDataApiKey && {
              metaDataApi: { enabled: true, key: metaDataApiKey },
            }),
          },
          options: [],
          open: true,
          nested: true,
        },
        {
          name: 'Custom Range',
          type: SearchFilterType.NUMBER_RANGE,
          index: 1,
          layer: 4,
          key: `${prefix}_${platform.value}_followers_custom`,
          field: `${prefix}_${platform.value}_followers_custom`,
          metadata: { color: 'primary' },
          nested: true,
        },
        {
          name: `${platform.label} Followers N/A`,
          type: SearchFilterType.NOT_AVAILABLE,
          icon: undefined,
          index: 2,
          layer: 4,
          key: `${prefix}_${platform.value}_followers_availability`,
          field: `${prefix}_${platform.value}_followers_availability`,
          metadata: { color: 'primary' },
          nested: true,
        },
      ],
    };
  };

  const buildEntityTab = (
    platform: Platform,
    entity: Entity,
    index: number
  ): SearchFilter => ({
    name: entityLabelOf(entity),
    type: SearchFilterType.NONE,
    index,
    key: `${platform.value}_${entity}`,
    field: `${platform.value}_${entity}`,
    open: entity === 'people',
    metadata: {
      tabs: {
        icon: entity === 'companies' ? 'business' : 'people',
      },
    },
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: `${platform.label} ${entityLabelOf(entity)}`,
        },
      },
    },
    items: [buildFollowersRadioGroup(platform, entity)],
  });

  const buildPlatformTab = (
    platform: Platform,
    index: number,
    hasEntityToggle: boolean
  ): SearchFilter => ({
    name: platform.label,
    type: SearchFilterType.NONE,
    index,
    key: platform.value,
    field: platform.value,
    comingSoon: !platform.enabled,
    disabled: !platform.enabled,
    open: platform.open,
    utils: {
      tags: {
        config: {
          // suppress platform card when an entity sub-tab renders its own
          // card — otherwise the closed state shows card-in-card
          showAsCard: !hasEntityToggle,
          // only used when there's no entity toggle (Company scope today)
          tagsCardTitle: `${platform.label} Followers`,
        },
      },
    },
    items: hasEntityToggle
      ? [
          {
            name: '',
            type: SearchFilterType.BUTTON_GROUP,
            index: 0,
            key: `${platform.value}_entity_group`,
            field: `${platform.value}_entity_group`,
            config: { labelKey: 'label', valueKey: 'value' },
            metadata: { allowParallelItems: true },
            items: [
              {
                name: 'Applied Filters',
                type: SearchFilterType.APPLIED_FILTERS,
                index: 2,
                icon: undefined,
                key: `${platform.value}_entity_applied_filters`,
                field: `${platform.value}_entity_applied_filters`,
                metadata: {},
              },
              buildEntityTab(platform, 'companies', 0),
              buildEntityTab(platform, 'people', 1),
            ],
          },
        ]
      : [
          buildFollowersRadioGroup(
            platform,
            isCompany ? 'companies' : 'people'
          ),
        ],
  });

  const appliedFilters: SearchFilter = {
    name: 'Applied Filters',
    type: SearchFilterType.APPLIED_FILTERS,
    index: 2,
    icon: undefined,
    key: 'social_media_followers_applied_filters',
    field: 'social_media_followers_applied_filters',
    metadata: {},
  };

  return {
    id: 'Social Media Followers',
    name: 'Social Media Followers',
    icon: 'subscriptions',
    index: 23,
    type: SearchFilterType.ROOT,
    category: isCompany
      ? [FilterCategory.COMPANY_DATA]
      : [FilterCategory.CONTACT_DATA],
    pinned: true,
    badge: FilterBadgeType.NEW,
    forcePinnedAt: '2026-07-10T14:32:06.000Z',
    items: [
      {
        name: '',
        type: SearchFilterType.BUTTON_GROUP,
        index: 0,
        key: 'social_media_followers',
        field: 'social_media_followers',
        config: { labelKey: 'label', valueKey: 'value' },
        metadata: { allowParallelItems: true },
        items: [
          appliedFilters,
          ...SOCIAL_PLATFORMS.map((platform, idx) =>
            buildPlatformTab(
              platform,
              idx,
              !isCompany && platform.value === 'linkedin'
            )
          ),
        ],
      },
    ],
  };
}
